// =============================================================================
// src/hooks/useP2P.ts
//
//  BUGS FIXED THIS PASS:
//   B4  addHistory never called on transfer complete (sender AND receiver)
//   B5  LAN race: two peers simultaneously create rooms
//       → use peerId lexicographic ordering to elect who creates
//   B6  onIncoming drops file if PEER_JOINED hasn't processed yet
//       → build fallback User from manifest payload
//   B7  makeEngine depends on selectedPeer → stale closure + unnecessary recreations
//       → use selectedPeerRef so closure always reads current value
//   B8  onComplete uses stale selectedPeer from closure
//       → same ref fix; read queuePeerRef which tracks the actual target
//   B11 connectWS captured stale makeEngine after selectedPeer changes
//       → makeEngine no longer has selectedPeer in deps
// =============================================================================
import { useState, useEffect, useRef, useCallback, type FormEvent } from 'react';
import { getOrCreateIdentity, getLanRoomId, setLanRoomId, clearLanRoomId, type DeviceType } from '../lib/identity';
import { SignallingClient } from '../lib/signalling';
import { WebRTCEngine, type TransferProgress, type IceServersGetter } from '../lib/webrtc';
import * as api from '../lib/api';
import {
  markFailedLanRoom,
  pickJoinableLanRoomId,
  pruneFailedLanRooms,
  resolveLanCreator,
} from '../features/nearby/lanDiscovery';
import { resolveWsUrl } from '../features/private-room/resolveWsUrl';
import { getDebugSessionId, logDebug } from '../lib/debug';

export type User = { id: string; name: string; avatar: string; device: DeviceType };
export type TransferState = 'idle' | 'awaiting_accept' | 'transferring' | 'completed' | 'rejected' | 'cancelled';
export type AppMode = 'local' | 'room_setup' | 'in_room';
export type IncomingRequest = { peer: User; fileName: string; fileSize: number; fileType: string; state: 'pending' | 'receiving' | 'completed' | 'rejected' };
export type TransferRecord  = { id: string; type: 'sent' | 'received'; peer: User; fileName: string; fileSize: number; date: Date; status: 'completed' | 'failed' | 'cancelled' };
export type ChatMessage     = { id: string; sender: User; text: string; timestamp: Date };

const LAN_HEARTBEAT_MS      = 12_000;
const LAN_POLL_MS           =  5_000;
const LAN_ROOM_RETRY_BACKOFF_MS = 15_000;
const LAN_ELECTION_FAILOVER_STEP_MS = 20_000;
const LAN_CREATE_MIN_BACKOFF_MS = 10_000;
const LAN_CREATE_RATE_LIMIT_BACKOFF_MS = 60_000;
const LAN_CREATE_MAX_BACKOFF_MS = 5 * 60_000;
const LAN_JOIN_RATE_LIMIT_BACKOFF_MS = 30_000;

type ApiErrorMeta = {
  code: string;
  status: number;
  rateLimited: boolean;
  staleRoom: boolean;
};

function parseApiError(err: unknown): ApiErrorMeta {
  const code = typeof err === 'object' && err !== null && 'code' in err
    ? String((err as { code?: unknown }).code ?? '')
    : '';
  const status = typeof err === 'object' && err !== null && 'status' in err
    ? Number((err as { status?: unknown }).status ?? 0)
    : 0;
  return {
    code,
    status,
    rateLimited: code === 'RATE_LIMIT' || status === 429,
    staleRoom: code === 'ROOM_NOT_FOUND' || status === 404 || status === 410,
  };
}

function nanoid(len = 10): string {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  return Array.from(crypto.getRandomValues(new Uint8Array(len)), b => chars[b % chars.length]).join('');
}

function peerToUser(peerId: string, nickname: string, deviceType: string): User {
  return {
    id:     peerId,
    name:   nickname,
    avatar: `https://api.dicebear.com/7.x/bottts/svg?seed=${encodeURIComponent(nickname)}&backgroundColor=e2e8f0`,
    device: (deviceType as DeviceType) || 'desktop',
  };
}

// =============================================================================
export function useP2P() {
  // ── Stable identity — never changes after first render ────────────────────
  const identityRef = useRef(getOrCreateIdentity());
  const identity    = identityRef.current;

  useEffect(() => {
    logDebug('p2p', 'hook.init', {
      debugSessionId: getDebugSessionId(),
      identity: {
        peerId: identity.peerId,
        nickname: identity.nickname,
        device: identity.device,
      },
    });
  }, [identity.peerId, identity.nickname, identity.device]);

  // ── Core state ────────────────────────────────────────────────────────────
  const [me] = useState<User>(() => ({
    id: identity.peerId, name: identity.nickname,
    avatar: identity.avatar, device: identity.device,
  }));
  const [peers,   setPeers]     = useState<User[]>([]);
  const [appMode, setAppModeSt] = useState<AppMode>('local');
  const [darkMode, setDarkMode] = useState(false);

  const [roomTab,    setRoomTab]    = useState<'create' | 'join'>('create');
  const [roomConfig, setRoomConfig] = useState({ id: '', password: '', usePassword: false });
  const [inviteUrl,  setInviteUrl]  = useState('');
  const [joinInput,  setJoinInput]  = useState({ id: '', password: '' });
  const [copied,     setCopied]     = useState(false);
  const [joinError,  setJoinError]  = useState<string | null>(null);

  const [selectedPeer,   setSelectedPeer]   = useState<User | null>(null);
  const [selectedFiles,  setSelectedFiles]  = useState<File[]>([]);
  const [transferState,  setTransferState]  = useState<TransferState>('idle');
  const [transferError, setTransferError]   = useState<string | null>(null);
  const [senderProgress, setSenderProgress] = useState(0);
  const [transferRate,   setTransferRate]   = useState('');
  const [timeRemaining,  setTimeRemaining]  = useState('');

  const [receiverProgress, setReceiverProgress] = useState(0);
  const [incomingReq,      setIncomingReq]      = useState<IncomingRequest | null>(null);

  const [history,       setHistory]       = useState<TransferRecord[]>([]);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);

  const [messages,   setMessages]   = useState<ChatMessage[]>([]);
  const [chatInput,  setChatInput]  = useState('');
  const [isChatOpen, setIsChatOpen] = useState(false);

  // ── Refs ──────────────────────────────────────────────────────────────────
  const signalRef     = useRef<SignallingClient | null>(null);
  const engineRef     = useRef<WebRTCEngine | null>(null);

  // B2: Live getter ref — always returns the most current ICE server list
  const iceServersRef = useRef<RTCIceServer[]>([
    { urls: 'stun:stun.cloudflare.com:3478' },
    { urls: 'stun:stun1.l.google.com:19302' },
  ]);
  const getIceServers: IceServersGetter = useCallback(() => iceServersRef.current, []);

  const currentRoomId    = useRef('');
  const currentInviteUrl = useRef('');
  const currentHostToken = useRef('');
  const activePeers      = useRef<Map<string, User>>(new Map());
  const activeTransferId = useRef('');

  // SESSION PEER ID — the peerId the server assigns for this WS connection.
  // This is a fresh nanoid(16) generated in _joinByRoomId each time we join.
  // It is DIFFERENT from identity.peerId (the persistent localStorage value).
  // All server messages use session peerIds.  We must store it from ROOM_INFO
  // and use it — not identity.peerId — for any self-filtering in signal handlers.
  const sessionPeerIdRef = useRef<string>('');

  // B7: selectedPeer ref so makeEngine closure always reads current value
  const selectedPeerRef  = useRef<User | null>(null);
  // B8: track the actual target peer for history recording
  const queuePeerRef     = useRef<string>('');
  const fileQueue        = useRef<File[]>([]);
  // Track files sent for history (updated when queue is loaded)
  const queueFilesRef    = useRef<File[]>([]);

  // BUG 5 (previous): LAN race prevention
  const lanCreatingRef   = useRef(false);
  const failedLanRoomsRef = useRef<Map<string, number>>(new Map());
  const noRoomSinceRef = useRef<number | null>(null);
  const nextLanCreateAttemptAtRef = useRef<number>(0);
  const lanCreateBackoffMsRef = useRef<number>(0);
  const lanPollInFlightRef = useRef(false);
  const nextLanJoinAttemptAtRef = useRef<number>(0);

  // WS failure tracking — if the same room's WS fails repeatedly, treat the
  // room as broken and clear localStorage so discovery can elect a fresh room.
  const wsFailCountRef  = useRef<number>(0);    // consecutive WS failures for current room
  const wsFailRoomIdRef = useRef<string>('');    // which room we're counting against
  const WS_FAIL_ROOM_ABANDON = 3;               // give up after this many consecutive failures

  // Keep selectedPeerRef in sync
  useEffect(() => { selectedPeerRef.current = selectedPeer; }, [selectedPeer]);

  // If the selected peer is no longer in the current peer list, clear selection.
  // This prevents sending to stale IDs captured from pre-room LAN discovery.
  useEffect(() => {
    if (!selectedPeer) return;
    const stillVisible = peers.some(p => p.id === selectedPeer.id);
    if (!stillVisible) {
      setSelectedPeer(null);
      fileQueue.current = [];
      queueFilesRef.current = [];
      queuePeerRef.current = '';
      if (transferState === 'awaiting_accept') {
        setTransferState('cancelled');
      }
    }
  }, [peers, selectedPeer, transferState]);

  // ── History helper ────────────────────────────────────────────────────────
  const addHistory = useCallback((
    type:      'sent' | 'received',
    peer:      User,
    fileName:  string,
    sizeBytes: number,
    status:    'completed' | 'failed' | 'cancelled',
  ) => {
    setHistory(prev => [{
      id: nanoid(), type, peer, fileName,
      fileSize: sizeBytes / (1024 * 1024),
      date: new Date(), status,
    }, ...prev]);
  }, []);

  // ── Reset sender state ────────────────────────────────────────────────────
  const resetSender = useCallback(() => {
    setSelectedPeer(null);
    setSelectedFiles([]);
    setTransferState('idle');
    setTransferError(null);
    setSenderProgress(0);
    setTransferRate('');
    setTimeRemaining('');
    fileQueue.current    = [];
    queueFilesRef.current = [];
    queuePeerRef.current = '';
    activeTransferId.current = '';
  }, []);

  // ── Multi-file queue: send next ───────────────────────────────────────────
  const _sendNextInQueue = useCallback(() => {
    if (fileQueue.current.length === 0 || !engineRef.current) {
      setTransferState('completed');
      return;
    }
    const file       = fileQueue.current.shift()!;
    const toPeerId   = queuePeerRef.current;
    const transferId = nanoid(16);
    activeTransferId.current = transferId;
    setTransferState('awaiting_accept');
    logDebug('transfer', 'queue.send_next', {
      transferId,
      toPeerId,
      fileName: file.name,
      fileSize: file.size,
      queueRemaining: fileQueue.current.length,
    });
    engineRef.current.sendFile(toPeerId, transferId, file);
  }, []);

  // ── Engine factory — B7: no selectedPeer dep, use ref instead ────────────
  const makeEngine = useCallback((signal: SignallingClient) => {
    return new WebRTCEngine(
      getIceServers,   // B2: live getter
      signal,
      identity.peerId,

      // onProgress — route by role
      (p: TransferProgress) => {
        if (p.role === 'sender') {
          setSenderProgress(p.pct);
          setTransferRate(`${(p.speedBps / (1024 * 1024)).toFixed(1)} MB/s`);
          const s = Math.ceil(p.etaSec);
          setTimeRemaining(s > 60 ? `${Math.ceil(s / 60)}m` : `${s}s`);
          if (p.pct < 99.5) setTransferState('transferring');
        } else {
          setReceiverProgress(p.pct);
        }
      },

      // onComplete — B4: always record history; B8: read refs not stale closure
      (transferId, role) => {
        logDebug('transfer', 'engine.complete', { transferId, role });
        if (role === 'sender') {
          setTransferError(null);
          setSenderProgress(100);
          setTransferRate('');
          setTimeRemaining('');

          // B4: record the completed file in history
          const peer  = activePeers.current.get(queuePeerRef.current) ?? selectedPeerRef.current;
          const files = queueFilesRef.current;
          if (peer && files.length > 0) {
            const totalBytes = files.reduce((a, f) => a + f.size, 0);
            const name = files.length > 1 ? `${files.length} files` : (files[0]?.name ?? 'file');
            addHistory('sent', peer, name, totalBytes, 'completed');
          }

          // Send next file in queue or mark complete
          if (fileQueue.current.length > 0) {
            _sendNextInQueue();
          } else {
            setTransferState('completed');
          }
        } else {
          setTransferError(null);
          // B4: record received file in history
          setReceiverProgress(100);
          setIncomingReq(prev => {
            if (prev) {
              addHistory('received', prev.peer, prev.fileName, prev.fileSize * 1024 * 1024, 'completed');
              return { ...prev, state: 'completed' };
            }
            return null;
          });
        }
      },

      // onIncoming — B6: build fallback User if PEER_JOINED not yet processed
      (f) => {
        logDebug('transfer', 'engine.incoming_manifest', {
          transferId: f.transferId,
          fromPeerId: f.fromPeerId,
          fileName: f.name,
          size: f.size,
          mimeType: f.mimeType,
        });
        const sender = activePeers.current.get(f.fromPeerId)
          ?? peerToUser(f.fromPeerId, `Peer ${f.fromPeerId.slice(0, 6)}`, 'desktop');
        setIncomingReq({
          peer:     sender,
          fileName: f.name,
          fileSize: +(f.size / (1024 * 1024)).toFixed(1),
          fileType: f.mimeType,
          state:    'pending',
        });
        activeTransferId.current = f.transferId;
        setReceiverProgress(0);
      },

      // onError
      (transferId, code) => {
        logDebug('transfer', 'engine.error', { transferId, code }, 'warn');
        if (code === 'DECLINED') {
          setTransferState('rejected');
          setTransferError('Receiver declined this transfer.');
          // B4: record failed transfer
          const peer  = selectedPeerRef.current;
          const files = queueFilesRef.current;
          if (peer && files.length > 0) {
            const total = files.reduce((a, f) => a + f.size, 0);
            addHistory('sent', peer, files[0]?.name ?? 'file', total, 'failed');
          }
        } else if (code === 'INTEGRITY_FAIL') {
          setTransferState('cancelled');
          setTransferError('Integrity check failed. Please send again.');
          setJoinError('File integrity check failed. The file may be corrupted. Please try again.');
        } else {
          setTransferState('cancelled');
          setTransferError('Transfer failed. Please retry.');
        }
      },
    );
  // B7: removed selectedPeer from deps — use selectedPeerRef instead
  }, [identity.peerId, getIceServers, _sendNextInQueue, addHistory]);

  // ── Connect WebSocket ─────────────────────────────────────────────────────
  const connectWS = useCallback((wsUrl: string) => {
    logDebug('p2p', 'ws.connect.start', { wsUrl });
    signalRef.current?.disconnect();
    engineRef.current?.destroyAll();
    sessionPeerIdRef.current = ''; // reset session peerId on each new connection

    const sig = new SignallingClient();
    signalRef.current = sig;
    engineRef.current = makeEngine(sig);

    // ── WS failure tracking ─────────────────────────────────────────────────
    // Extract the roomId from the WS URL so we know which room is failing.
    let connRoomId = '';
    try {
      const m = wsUrl.match(/\/room\/([^/?]+)/);
      connRoomId = m ? m[1]! : '';
    } catch { /* ignore */ }

    // Track success: a successful open resets the failure counter
    sig.onOpen = () => {
      if (wsFailRoomIdRef.current === connRoomId) {
        wsFailCountRef.current  = 0;
        wsFailRoomIdRef.current = '';
      }
    };

    // Track failure: consecutive WS failures for the same room → mark as broken
    //
    // Code 1006 = Abnormal Closure = server rejected the WebSocket upgrade
    // (returned non-101).  This will NEVER succeed on retry — abandon immediately.
    // Other codes (1001, 1011, etc.) may be transient, so allow a few retries.
    sig.onClose = (code: number, reason: string) => {
      if (!sig.connected) {
        if (connRoomId) {
          if (wsFailRoomIdRef.current !== connRoomId) {
            wsFailRoomIdRef.current = connRoomId;
            wsFailCountRef.current  = 0;
          }
          wsFailCountRef.current++;

          // Code 1006 = server rejected upgrade — no point retrying, abandon now
          const isHardReject = code === 1006;
          const shouldAbandon = isHardReject || wsFailCountRef.current >= WS_FAIL_ROOM_ABANDON;

          logDebug('nearby', 'ws.failure_count', {
            roomId:       connRoomId,
            failCount:    wsFailCountRef.current,
            code,
            reason,
            isHardReject,
            abandon:      shouldAbandon,
          }, 'warn');

          if (shouldAbandon) {
            const storedRoom = getLanRoomId();
            if (storedRoom === connRoomId) {
              logDebug('nearby', 'ws.abandon_room', { roomId: connRoomId, code }, 'warn');
              clearLanRoomId();
              markFailedLanRoom(failedLanRoomsRef.current, connRoomId, 60_000);
              lanCreatingRef.current  = false;
              wsFailCountRef.current  = 0;
              wsFailRoomIdRef.current = '';
              sig.disconnect();
            }
          }
        }
      }
    };

    sig.on('ROOM_INFO', (msg) => {
      const payload = msg.payload as {
        peerId:      string;   // SESSION peerId assigned by the server for this connection
        members:     Array<{ peerId: string; nickname: string; deviceType: string }>;
        chatHistory: Array<{ id: string; peer_id?: string; from?: string; nickname: string; text: string; created_at?: number; ts?: number }>;
      };

      // SELF-FILTER FIX: store the session peerId so all handlers below can
      // use it.  identity.peerId is the persistent localStorage value — it is
      // a DIFFERENT string from payload.peerId (server-assigned nanoid).
      // Using identity.peerId for filtering never matched, causing the peer
      // to see themselves in the radar and in chat message dedup.
      sessionPeerIdRef.current = payload.peerId;
      logDebug('p2p', 'ws.room_info', {
        sessionPeerId: payload.peerId,
        membersCount: payload.members.length,
        chatHistoryCount: payload.chatHistory?.length ?? 0,
      });

      activePeers.current.clear();
      const peerList: User[] = [];
      for (const m of payload.members) {
        // Filter using session peerId (belt-and-suspenders: server already
        // excludes self from members, but this guards against future changes)
        if (m.peerId === sessionPeerIdRef.current) continue;
        const u = peerToUser(m.peerId, m.nickname, m.deviceType);
        activePeers.current.set(m.peerId, u);
        peerList.push(u);
      }
      setPeers(peerList);

      if (payload.chatHistory?.length) {
        const nicknameMap = new Map<string, string>();
        for (const m of payload.chatHistory) {
          const sid = m.peer_id ?? m.from ?? '';
          if (sid && m.nickname) nicknameMap.set(sid, m.nickname);
        }
        setMessages(payload.chatHistory.map(m => {
          const senderId = m.peer_id ?? m.from ?? '';
          let sender: User;
          // Use session peerId to recognise own historic messages
          if (senderId === sessionPeerIdRef.current) {
            sender = { id: identity.peerId, name: identity.nickname, avatar: identity.avatar, device: identity.device };
          } else {
            const nick = nicknameMap.get(senderId) ?? m.nickname ?? 'Unknown';
            sender = activePeers.current.get(senderId) ?? peerToUser(senderId, nick, 'desktop');
          }
          return { id: m.id, sender, text: m.text, timestamp: new Date(m.created_at ?? m.ts ?? Date.now()) };
        }));
      }
    });

    sig.on('PEER_JOINED', (msg) => {
      const { peerId, nickname, deviceType } = msg.payload as { peerId: string; nickname: string; deviceType: string };
      // Use session peerId — identity.peerId never matches server session peerIds
      if (peerId === sessionPeerIdRef.current) return;
      logDebug('p2p', 'peer.joined', { peerId, nickname, deviceType });
      const u = peerToUser(peerId, nickname, deviceType);
      activePeers.current.set(peerId, u);
      setPeers(prev => prev.find(p => p.id === peerId) ? prev : [...prev, u]);
      engineRef.current?.checkAndResumePendingReceives(peerId);
    });

    sig.on('PEER_LEFT', (msg) => {
      const { peerId } = msg.payload as { peerId: string };
      logDebug('p2p', 'peer.left', { peerId }, 'warn');
      activePeers.current.delete(peerId);
      setPeers(prev => prev.filter(p => p.id !== peerId));
      engineRef.current?.destroyPeer(peerId);
    });

    sig.on('CHAT_MESSAGE', (msg) => {
      const p = msg.payload as { id: string; text: string; from: string; nickname: string; ts: number };
      // Use session peerId — server now uses broadcastExcept so we shouldn't
      // receive our own messages, but guard here as a second layer of defence
      if (p.from === sessionPeerIdRef.current) return;
      const sender = activePeers.current.get(p.from) ?? peerToUser(p.from, p.nickname, 'desktop');
      setMessages(prev => [...prev, { id: p.id, sender, text: p.text, timestamp: new Date(p.ts) }]);
    });

    sig.connect(wsUrl);
    logDebug('p2p', 'ws.connect.called', { wsUrl });
  }, [identity, makeEngine]);

  // ── TURN creds on mount ───────────────────────────────────────────────────
  useEffect(() => {
    api.getTurnCreds(identity.peerId)
      .then(c => { iceServersRef.current = c.iceServers; }) // B2: update ref, engines get it live
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── LAN discovery ─────────────────────────────────────────────────────────
  //
  //  BUG 1 FIX: re-heartbeat immediately after setLanRoomId so the LanHubDO
  //             record carries the new roomId before the next poll cycle.
  //             Previously the 30-second gap meant Device B saw lanRoomId=null
  //             and created a duplicate room.
  //
  //  BUG 2 FIX: track WS liveness via signalRef.current.connected rather than
  //             a one-shot boolean.  If the WS drops, discovery auto-restarts.
  //
  //  BUG 3 FIX: reset lanCreatingRef when the stale-room-id path fails.
  //
  //  BUG 5 FIX: declare connectWS in effect deps so the closure is never stale.
  //
  //  BUG 6 FIX: election only runs when we can actually see at least one other
  //             peer, preventing both devices from independently "winning" before
  //             they have seen each other.
  useEffect(() => {
    if (appMode !== 'local') return;

    const heartbeat = async () => {
      try {
        await api.lanHeartbeat({
          peerId:     identity.peerId,
          nickname:   identity.nickname,
          deviceType: identity.device,
          lanRoomId:  getLanRoomId() ?? undefined,
        });
        logDebug('nearby', 'heartbeat.ok', {
          peerId: identity.peerId,
          lanRoomId: getLanRoomId() ?? null,
        });
      } catch {
        logDebug('nearby', 'heartbeat.error', {}, 'warn');
        /* offline - will retry */
      }
    };

    const wsAlive = () => !!(signalRef.current?.connected);

    // Returns true while a WebSocket is in the CONNECTING state (not yet open,
    // not yet failed).  Polls must NOT call connectWS while this is true —
    // interrupting a connecting WS resets its backoff and creates an infinite
    // loop where the connection never gets more than 3 seconds to establish.
    const wsConnecting = () => !!(signalRef.current?.connecting);

    const poll = async () => {
      if (wsAlive()) {
        noRoomSinceRef.current = null;
        nextLanCreateAttemptAtRef.current = 0;
        lanCreateBackoffMsRef.current = 0;
        nextLanJoinAttemptAtRef.current = 0;
        return;
      }
      // Don't interrupt a WS that is actively connecting — give it time to complete
      if (wsConnecting()) {
        logDebug('nearby', 'poll.skipped_ws_connecting', {}, 'info');
        return;
      }
      if (lanPollInFlightRef.current) {
        logDebug('nearby', 'poll.skipped_inflight', {}, 'warn');
        return;
      }
      lanPollInFlightRef.current = true;

      try {
        const { peers: lanPeers } = await api.getLanPeers(identity.peerId);
        logDebug('nearby', 'poll.peers', {
          count: lanPeers.length,
          peerIds: lanPeers.map(p => p.peerId),
          roomIds: lanPeers.map(p => p.lanRoomId ?? null),
        });
        pruneFailedLanRooms(failedLanRoomsRef.current);

        // Step 1: rejoin our own stored LAN room if still valid.
        let myRoomId = getLanRoomId();
        if (myRoomId) {
          const joinWaitMs = Math.max(0, nextLanJoinAttemptAtRef.current - Date.now());
          if (joinWaitMs > 0) {
            logDebug('nearby', 'rejoin.skipped_backoff', {
              waitMs: joinWaitMs,
              nextAttemptAt: nextLanJoinAttemptAtRef.current,
            }, 'warn');
            return;
          }
          logDebug('nearby', 'rejoin.attempt', { roomId: myRoomId });
          try {
            const res = await api.joinRoomDirect({
              roomId:     myRoomId,
              nickname:   identity.nickname,
              publicKey:  identity.publicKey,
              deviceType: identity.device,
              peerId:     identity.peerId,
            });
            logDebug('nearby', 'rejoin.success', { roomId: myRoomId });
            nextLanJoinAttemptAtRef.current = 0;
            nextLanCreateAttemptAtRef.current = 0;
            lanCreateBackoffMsRef.current = 0;
            connectWS(resolveWsUrl(res.wsUrl));
            return;
          } catch (err) {
            const meta = parseApiError(err);
            if (meta.rateLimited) {
              nextLanJoinAttemptAtRef.current = Date.now() + LAN_JOIN_RATE_LIMIT_BACKOFF_MS;
            }
            logDebug('nearby', 'rejoin.failed', {
              roomId: myRoomId,
              errorCode: meta.code,
              errorStatus: meta.status,
              rateLimited: meta.rateLimited,
              staleRoom: meta.staleRoom,
            }, 'warn');
            // Only clear when room is definitely stale.
            if (meta.staleRoom) {
              clearLanRoomId();
              failedLanRoomsRef.current.delete(myRoomId);
              lanCreatingRef.current = false;
              myRoomId = null;
              await heartbeat();
            }
            return;
          }
        }

        if (lanPeers.length === 0) {
          noRoomSinceRef.current = null;
          setPeers([]);
          return;
        }

        // Step 2: join freshest joinable LAN room from visible peers.
        const joinableRoomId = pickJoinableLanRoomId(lanPeers, failedLanRoomsRef.current);
        if (joinableRoomId) {
          const joinWaitMs = Math.max(0, nextLanJoinAttemptAtRef.current - Date.now());
          if (joinWaitMs > 0) {
            logDebug('nearby', 'join_visible_room.skipped_backoff', {
              roomId: joinableRoomId,
              waitMs: joinWaitMs,
              nextAttemptAt: nextLanJoinAttemptAtRef.current,
            }, 'warn');
            return;
          }
          noRoomSinceRef.current = null;
          logDebug('nearby', 'join_visible_room.attempt', { roomId: joinableRoomId });
          try {
            const res = await api.joinRoomDirect({
              roomId:     joinableRoomId,
              nickname:   identity.nickname,
              publicKey:  identity.publicKey,
              deviceType: identity.device,
              peerId:     identity.peerId,
            });
            setLanRoomId(joinableRoomId);
            await heartbeat();
            logDebug('nearby', 'join_visible_room.success', { roomId: joinableRoomId });
            nextLanJoinAttemptAtRef.current = 0;
            nextLanCreateAttemptAtRef.current = 0;
            lanCreateBackoffMsRef.current = 0;
            connectWS(resolveWsUrl(res.wsUrl));
            return;
          } catch (err) {
            // Avoid retry storms against stale room IDs.
            const meta = parseApiError(err);
            if (meta.rateLimited) {
              nextLanJoinAttemptAtRef.current = Date.now() + LAN_JOIN_RATE_LIMIT_BACKOFF_MS;
            }
            logDebug('nearby', 'join_visible_room.failed', {
              roomId: joinableRoomId,
              errorCode: meta.code,
              errorStatus: meta.status,
              rateLimited: meta.rateLimited,
              staleRoom: meta.staleRoom,
            }, 'warn');
            if (meta.staleRoom) {
              markFailedLanRoom(failedLanRoomsRef.current, joinableRoomId, LAN_ROOM_RETRY_BACKOFF_MS);
            }
          }
        }

        // Step 3: deterministic election across all visible peers.
        if (!lanCreatingRef.current && lanPeers.length > 0) {
          if (!joinableRoomId) {
            if (noRoomSinceRef.current == null) noRoomSinceRef.current = Date.now();
          }
          const noRoomForMs = noRoomSinceRef.current == null ? 0 : Math.max(0, Date.now() - noRoomSinceRef.current);
          const election = resolveLanCreator(
            identity.peerId,
            lanPeers,
            noRoomForMs,
            LAN_ELECTION_FAILOVER_STEP_MS,
          );
          logDebug('nearby', 'election.result', {
            iWin: election.shouldCreate,
            selfPeerId: identity.peerId,
            winnerPeerId: election.winnerPeerId,
            candidates: election.candidates,
            noRoomForMs,
            failoverRound: election.round,
            promotedFallback: election.promoted,
          });
          if (election.shouldCreate) {
            const now = Date.now();
            const waitMs = Math.max(0, nextLanCreateAttemptAtRef.current - now);
            if (waitMs > 0) {
              logDebug('nearby', 'create_room.skipped_backoff', {
                waitMs,
                nextAttemptAt: nextLanCreateAttemptAtRef.current,
                backoffMs: lanCreateBackoffMsRef.current,
              }, 'warn');
              return;
            }

            lanCreatingRef.current = true;
            try {
              const created = await api.createRoom({
                type:          'drop',
                hostNickname:  identity.nickname,
                hostPublicKey: identity.publicKey,
                deviceType:    identity.device,
                ttlSec:        86400,
              });
              setLanRoomId(created.roomId);
              await heartbeat();
              nextLanCreateAttemptAtRef.current = 0;
              lanCreateBackoffMsRef.current = 0;
              logDebug('nearby', 'create_room.success', { roomId: created.roomId });

              try {
                const joined = await api.joinRoomDirect({
                  roomId:     created.roomId,
                  nickname:   identity.nickname,
                  publicKey:  identity.publicKey,
                  deviceType: identity.device,
                  peerId:     identity.peerId,
                  role:       'host',
                  hostToken:  created.hostToken,
                });
                logDebug('nearby', 'create_room.joined', { roomId: created.roomId });
                nextLanJoinAttemptAtRef.current = 0;
                nextLanCreateAttemptAtRef.current = 0;
                lanCreateBackoffMsRef.current = 0;
                connectWS(resolveWsUrl(joined.wsUrl));
                return;
              } catch (joinErr) {
                const meta = parseApiError(joinErr);
                if (meta.rateLimited) {
                  nextLanJoinAttemptAtRef.current = Date.now() + LAN_JOIN_RATE_LIMIT_BACKOFF_MS;
                }
                logDebug('nearby', 'create_room.join_failed', {
                  roomId: created.roomId,
                  errorCode: meta.code,
                  errorStatus: meta.status,
                  rateLimited: meta.rateLimited,
                  staleRoom: meta.staleRoom,
                }, 'warn');
                // Keep lanRoomId for a later rejoin attempt unless room is stale.
                if (meta.staleRoom) {
                  clearLanRoomId();
                  await heartbeat();
                }
                lanCreatingRef.current = false;
                return;
              }
            } catch (err) {
              const meta = parseApiError(err);
              const baseBackoff = meta.rateLimited ? LAN_CREATE_RATE_LIMIT_BACKOFF_MS : LAN_CREATE_MIN_BACKOFF_MS;
              const multiplier = meta.rateLimited ? 2 : 1.5;
              const prevBackoff = lanCreateBackoffMsRef.current;
              const nextBackoff = prevBackoff > 0
                ? Math.min(
                    LAN_CREATE_MAX_BACKOFF_MS,
                    Math.max(baseBackoff, Math.round(prevBackoff * multiplier)),
                  )
                : baseBackoff;
              lanCreateBackoffMsRef.current = nextBackoff;
              nextLanCreateAttemptAtRef.current = Date.now() + nextBackoff;

              logDebug('nearby', 'create_room.failed', {
                errorCode: meta.code,
                errorStatus: meta.status,
                rateLimited: meta.rateLimited,
              }, 'error');
              logDebug('nearby', 'create_room.backoff', {
                backoffMs: nextBackoff,
                nextAttemptAt: nextLanCreateAttemptAtRef.current,
                rateLimited: meta.rateLimited,
              }, 'warn');
              clearLanRoomId();
              lanCreatingRef.current = false;
              await heartbeat();
            }
          }
        }

        if (!wsAlive()) {
          setPeers(lanPeers.map(p => peerToUser(p.peerId, p.nickname, p.deviceType)));
        }
      } catch {
        logDebug('nearby', 'poll.error', {}, 'warn');
        /* network offline - retry next tick */
      } finally {
        lanPollInFlightRef.current = false;
      }
    };

    const bootstrap = async () => {
      await heartbeat();
      await poll();
    };
    bootstrap();

    const hTimer = setInterval(heartbeat, LAN_HEARTBEAT_MS);
    const pTimer = setInterval(poll,      LAN_POLL_MS);

    return () => {
      clearInterval(hTimer);
      clearInterval(pTimer);
      lanCreatingRef.current = false;
      lanPollInFlightRef.current = false;
      api.lanLeave(identity.peerId).catch(() => {});
    };
  }, [appMode, connectWS]);

  // ── Cleanup on unmount ────────────────────────────────────────────────────
  useEffect(() => {
    return () => { signalRef.current?.disconnect(); engineRef.current?.destroyAll(); };
  }, []);

  // ==========================================================================
  // Handlers
  // ==========================================================================

  const setAppMode = useCallback((mode: AppMode) => {
    logDebug('ui', 'appMode.set', { from: appMode, to: mode });
    setAppModeSt(mode);
    if (mode === 'local') {
      signalRef.current?.disconnect();
      engineRef.current?.destroyAll();
      activePeers.current.clear();
      currentRoomId.current = ''; currentInviteUrl.current = '';
      currentHostToken.current = '';
      setInviteUrl('');
      nextLanCreateAttemptAtRef.current = 0;
      lanCreateBackoffMsRef.current = 0;
      nextLanJoinAttemptAtRef.current = 0;
      lanPollInFlightRef.current = false;
      setPeers([]);
    }
  }, [appMode]);

  const generateRoom = useCallback(() => {
    // Prepare local UI state only. Real room creation happens in startRoom().
    currentRoomId.current = '';
    currentInviteUrl.current = '';
    currentHostToken.current = '';
    setInviteUrl('');
    setJoinError(null);
    setRoomConfig(prev => ({
      ...prev,
      id: prev.id || Math.floor(100000 + Math.random() * 900000).toString(),
      password: prev.password || nanoid(6).toUpperCase(),
    }));
  }, []);

  const copyRoomLink = useCallback(() => {
    const link = currentInviteUrl.current || inviteUrl;
    if (!link) {
      setJoinError('Start the room first to generate a shareable link.');
      return;
    }
    navigator.clipboard.writeText(link).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [inviteUrl]);

  const startRoom = useCallback(async () => {
    setJoinError(null);
    try {
      let roomId = currentRoomId.current;
      let hostToken = currentHostToken.current;

      // If password mode is enabled, always create a fresh room with password so
      // we never accidentally start a previously pre-created non-password room.
      if (!roomId || roomConfig.usePassword) {
        const created = await api.createRoom({
          type: 'private',
          password:      roomConfig.usePassword ? roomConfig.password : undefined,
          hostNickname:  identity.nickname,
          hostPublicKey: identity.publicKey,
          deviceType:    identity.device,
        });
        roomId = created.roomId;
        currentRoomId.current    = roomId;
        currentInviteUrl.current = created.inviteUrl;
        currentHostToken.current = created.hostToken;
        setInviteUrl(created.inviteUrl);
        hostToken                = created.hostToken;
        setRoomConfig(prev => ({ ...prev, id: created.code }));
      }
      if (currentInviteUrl.current) setInviteUrl(currentInviteUrl.current);

      if (!hostToken) {
        throw new Error('Missing host token');
      }

      const joined = await api.joinRoomDirect({
        roomId, nickname: identity.nickname,
        publicKey: identity.publicKey, deviceType: identity.device,
        peerId: identity.peerId,
        role: 'host', hostToken,
      });
      setAppModeSt('in_room');
      activePeers.current.clear();
      setPeers([]);
      connectWS(resolveWsUrl(joined.wsUrl));
    } catch {
      setJoinError('Failed to create room. Check your connection and try again.');
    }
  }, [roomConfig, identity, connectWS]);

  const joinRoom = useCallback(async () => {
    if (joinInput.id.length < 6) return;
    setJoinError(null);
    try {
      const res = await api.joinRoom({
        code:       joinInput.id.trim(),
        password:   joinInput.password || undefined,
        nickname:   identity.nickname,
        publicKey:  identity.publicKey,
        deviceType: identity.device,
        peerId:     identity.peerId,
      });
      currentRoomId.current = res.roomId;
      currentHostToken.current = '';
      setInviteUrl('');
      setRoomConfig(prev => ({ ...prev, id: joinInput.id }));
      setAppModeSt('in_room');
      activePeers.current.clear();
      setPeers([]);
      connectWS(resolveWsUrl(res.wsUrl));
    } catch (err: unknown) {
      const code = (err as { code?: string }).code ?? '';
      if      (code === 'PASSWORD_REQUIRED')  setJoinError('This room requires a password.');
      else if (code === 'WRONG_PASSWORD')     setJoinError('Incorrect password. Please try again.');
      else if (code === 'ROOM_NOT_FOUND')     setJoinError('Room not found. Check the code and try again.');
      else if (code === 'ROOM_EXPIRED')       setJoinError('This room has expired.');
      else                                    setJoinError('Could not join room. Please try again.');
    }
  }, [joinInput, identity, connectWS]);

  const leaveRoom = useCallback(() => {
    signalRef.current?.disconnect();
    engineRef.current?.destroyAll();
    activePeers.current.clear();
    sessionPeerIdRef.current = '';
    currentRoomId.current = ''; currentInviteUrl.current = '';
    currentHostToken.current = '';
    setInviteUrl('');
    fileQueue.current = []; queueFilesRef.current = [];
    queuePeerRef.current = ''; activeTransferId.current = '';
    clearLanRoomId();
    setAppModeSt('local'); setPeers([]);
    setSelectedPeer(null); setSelectedFiles([]);
    setTransferState('idle'); setSenderProgress(0); setReceiverProgress(0);
    setTransferRate(''); setTimeRemaining(''); setIncomingReq(null); setJoinError(null);
  }, []);

  // B7/B8: load all files into queue ref for history recording
  const handleSend = useCallback(() => {
    if (selectedFiles.length === 0 || !selectedPeer || !engineRef.current) return;
    setTransferError(null);
    logDebug('transfer', 'send.clicked', {
      selectedPeerId: selectedPeer.id,
      selectedFileCount: selectedFiles.length,
      selectedFileNames: selectedFiles.map(f => f.name),
    });
    // Guard: don't send while signalling is disconnected.
    if (!signalRef.current?.connected) {
      logDebug('transfer', 'send.blocked.signal_disconnected', {}, 'warn');
      setTransferError('Connection lost. Please wait for reconnection and try again.');
      setTransferState('cancelled');
      return;
    }
    // Guard: selected peer must be in the live room peer list.
    if (!peers.some(p => p.id === selectedPeer.id)) {
      logDebug('transfer', 'send.blocked.stale_peer', { selectedPeerId: selectedPeer.id }, 'warn');
      setSelectedPeer(null);
      setTransferState('idle');
      return;
    }
    fileQueue.current     = [...selectedFiles];
    queueFilesRef.current = [...selectedFiles]; // snapshot for history
    queuePeerRef.current  = selectedPeer.id;
    _sendNextInQueue();
  }, [selectedFiles, selectedPeer, peers, _sendNextInQueue]);

  const cancelTransfer = useCallback(() => {
    if (activeTransferId.current) engineRef.current?.cancelTransfer(activeTransferId.current);
    fileQueue.current = [];
    const peer  = selectedPeerRef.current;
    const files = queueFilesRef.current;
    setTransferState('cancelled');
    setTransferError(null);
    setSenderProgress(0);
    if (peer && files.length > 0) {
      const total = files.reduce((a, f) => a + f.size, 0);
      addHistory('sent', peer, files.length > 1 ? `${files.length} files` : (files[0]?.name ?? 'file'), total, 'cancelled');
    }
  }, [addHistory]);

  const resetSelection = useCallback(() => { resetSender(); }, [resetSender]);

  const handleAcceptIncoming = useCallback(() => {
    if (!incomingReq || !engineRef.current) return;
    logDebug('transfer', 'incoming.accept.clicked', {
      fromPeerId: incomingReq.peer.id,
      fileName: incomingReq.fileName,
      fileSizeMb: incomingReq.fileSize,
    });
    setIncomingReq(prev => prev ? { ...prev, state: 'receiving' } : null);
    setReceiverProgress(0);
    engineRef.current.acceptTransfer(activeTransferId.current);
  }, [incomingReq]);

  const handleRejectIncoming = useCallback(() => {
    if (!incomingReq || !engineRef.current) return;
    logDebug('transfer', 'incoming.reject.clicked', {
      fromPeerId: incomingReq.peer.id,
      fileName: incomingReq.fileName,
    }, 'warn');
    engineRef.current.declineTransfer(activeTransferId.current);
    addHistory('received', incomingReq.peer, incomingReq.fileName, incomingReq.fileSize * 1024 * 1024, 'cancelled');
    setIncomingReq(prev => prev ? { ...prev, state: 'rejected' } : null);
    setReceiverProgress(0);
    setTimeout(() => setIncomingReq(null), 2000);
  }, [incomingReq, addHistory]);

  const handleSendMessage = useCallback((e: FormEvent) => {
    e.preventDefault();
    if (!chatInput.trim()) return;
    const id = nanoid(); const text = chatInput.trim();
    setMessages(prev => [...prev, { id, sender: me, text, timestamp: new Date() }]);
    setChatInput('');
    signalRef.current?.send({ type: 'CHAT_MESSAGE', payload: { id, text } });
  }, [chatInput, me]);

  const sendEmojiToRoom = useCallback((emoji: string) => {
    if (!signalRef.current?.connected) return;
    const id = nanoid();
    setMessages(prev => [...prev, { id, sender: me, text: emoji, timestamp: new Date() }]);
    signalRef.current.send({ type: 'CHAT_MESSAGE', payload: { id, text: emoji } });
  }, [me]);

  // ==========================================================================
  return {
    me, peers,
    appMode, setAppMode,
    darkMode, setDarkMode,

    roomTab, setRoomTab,
    roomConfig, setRoomConfig,
    inviteUrl,
    joinInput, setJoinInput,
    copied, joinError, setJoinError,

    selectedPeer, setSelectedPeer,
    selectedFiles, setSelectedFiles,
    transferState,
    transferError,
    progress: senderProgress,
    receiverProgress,
    transferRate, timeRemaining,

    incomingReq, setIncomingReq,
    history, isHistoryOpen, setIsHistoryOpen,
    messages, chatInput, setChatInput,
    isChatOpen, setIsChatOpen,

    handleSend, cancelTransfer, resetSelection,
    handleAcceptIncoming, handleRejectIncoming,
    generateRoom, copyRoomLink,
    startRoom, joinRoom, leaveRoom,
    handleSendMessage, sendEmojiToRoom,
  };
}

